import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications,
  People,
  HowToReg,
  Groups,
  CalendarToday,
  CheckCircle,
  TrendingUp,
  Add,
  Refresh,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ShadowBox } from '../../../../components/styles/styledComponent';

const Overview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    currentOnboarding: 0,
    totalTeams: 0,
  });
  const [attendanceData, setAttendanceData] = useState([]);
  const [completedOnboarding, setCompletedOnboarding] = useState([]);
  const [employeeGrowth, setEmployeeGrowth] = useState([]);
  const [selectedYear, setSelectedYear] = useState('2024');

  // Register Chart.js components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
  );

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const { getAdminDashboardStats } = await import('../../../../api/dashboardApi');
        const result = await getAdminDashboardStats();
        
        if (result.success) {
          const { stats, recentlyCompleted, recentActivities } = result.data;
          
          setStats({
            totalEmployees: stats.totalEmployees,
            currentOnboarding: stats.onboardingEmployees,
            totalTeams: stats.totalTeams,
          });

          // Use recent activities as notifications
          if (recentActivities && recentActivities.length > 0) {
            setNotifications(recentActivities.map(activity => ({
              id: activity.id,
              type: activity.module?.toLowerCase() || 'system',
              message: `${activity.title}: ${activity.subtitle}`,
              time: new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            })));
          }

          if (recentlyCompleted) {
            setCompletedOnboarding(recentlyCompleted.map(emp => ({
              id: emp.id,
              name: emp.name,
              role: emp.role,
              team: emp.team,
              completedDate: new Date(emp.completedDate).toLocaleDateString('en-GB')
            })));
          }

          if (result.data.employeeGrowth) {
            setEmployeeGrowth(result.data.employeeGrowth);
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();

    fetchDashboardStats();
  }, []);



  // Employee growth data for chart
  const growthChartData = {
    labels: employeeGrowth.map(g => g.month),
    datasets: [
      {
        label: 'Total Employees',
        data: employeeGrowth.map(g => g.employees),
        borderColor: '#7522D1',
        backgroundColor: 'rgba(117, 34, 209, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#7522D1',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function (context) {
            return `Employees: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: Math.max(...employeeGrowth.map(g => g.employees), 10) + 5,
        ticks: {
          stepSize: 1,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const StatCard = ({ title, value, icon, color, trend }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: color }}>
              {value}
            </Typography>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                <Typography variant="body2" color="success.main">
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{
            backgroundColor: `${color}15`,
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>

      {/* Header */}

      <Box sx={{ p: 3 }}>
        <ShadowBox sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 4
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#030007' }}>
              Team Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ color: '#4F4937' }}>
              Get a comprehensive view of your team's status and performance.
            </Typography>
          </Box>


        </ShadowBox>

        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 9 }}>
            <Grid container spacing={2} sx={{ mb: 2, backgroundColor: 'transparent' }}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Total Employees"
                  value={stats.totalEmployees}
                  icon={<People sx={{ fontSize: 32, color: '#7522D1' }} />}
                  color="#7522D1"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Current Onboarding"
                  value={stats.currentOnboarding}
                  icon={<HowToReg sx={{ fontSize: 32, color: '#2196F3' }} />}
                  color="#2196F3"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Total Teams"
                  value={stats.totalTeams}
                  icon={<Groups sx={{ fontSize: 32, color: '#4CAF50' }} />}
                  color="#4CAF50"
                />
              </Grid>
            </Grid>
            {/* Employee Growth Chart */}
            <Grid container spacing={2} sx={{ mb: 2, backgroundColor: 'transparent' }}>
              <Grid item size={{ xs: 12, md: 12 }}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#030007' }}>
                      Employee Growth Trend
                    </Typography>
                  </Box>
                  <Box sx={{ height: 300 }}>
                    <Line data={growthChartData} options={chartOptions} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item size={{ xs: 12, md: 3 }}>
            {/* Notifications */}
            <Grid container spacing={2} sx={{ backgroundColor: 'transparent', mb: 2 }}>
              <Grid item size={{ xs: 12, md: 12 }}>
                {notifications.length > 0 && (
                  <Paper sx={{ p: 1, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#030007' }}>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', mr: 2 }}>
                        Recent Notifications
                        <Avatar sx={{ bgcolor: '#7522D1' }}>
                          <Notifications size={"small"} />
                        </Avatar>
                      </Box>
                    </Typography>
                    <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                      {notifications.map((notification) => (
                        <ListItem key={notification.id} sx={{ borderLeft: `4px solid #7522D1`, mb: 1 }}>

                          <ListItemText
                            primary={notification.message}
                            secondary={notification.time}
                            primaryTypographyProps={{ fontWeight: 'normal', fontSize: '14px' }}
                            secondaryTypographyProps={{ color: '#4F4937', fontSize: '12px' }}
                          />
                          <Chip
                            label={notification.type.replace('_', ' ')}
                            size="small"
                            sx={{
                              backgroundColor: '#F4EBFF',
                              color: '#7522D1',
                              fontWeight: 'normal'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ backgroundColor: 'transparent' }}>
              <Grid item size={{ xs: 12, md: 12 }}>
                <Paper sx={{ p: 1, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#030007' }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between', mr: 2 }}>
                      Recently Onboarded
                      <Avatar sx={{ bgcolor: '#4CAF50' }}>
                        <CheckCircle />
                      </Avatar>
                    </Box>
                  </Typography>
                  <List>
                    {completedOnboarding.map((employee) => (
                      <ListItem key={employee.id} sx={{ borderLeft: `3px solid #4CAF50`, mb: 1 }}>

                        <ListItemText
                          primary={employee.name}
                          secondary={`${employee.role} • ${employee.team} : \n ${employee.completedDate}`}
                          primaryTypographyProps={{ fontWeight: 'normal', fontSize: '14px' }}
                          secondaryTypographyProps={{ color: '#4F4937', fontSize: '12px' }}
                        />
                        <Chip
                          label="Completed"
                          size="small"
                          sx={{
                            backgroundColor: '#E8F5E8',
                            color: '#4CAF50',
                            fontWeight: 'medium'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

            </Grid>

          </Grid>
        </Grid></Box>


      {/* Completed Onboarding List */}
    </>
  );
};

export default Overview;
